import { useCallback, useEffect, useMemo, useState, type JSX } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { isHTTPError } from 'ky';
import {
  PESEL_WARNINGS,
  type PatientDto,
  type PatientListOrder,
  type PatientListQuery,
  type PatientListSort,
  type PatientVisitDto,
  type PeselWarningCode,
  type RegisterPatientRequest,
} from '@ctms/contracts';
import { visitLabel } from '@ctms/shared';
import { z } from 'zod';
import { ArrowDown, ArrowUp, Search } from 'lucide-react';

import { getApiErrorMessage } from '../../../api/client.js';
import { createAppColumnHelper, useAppTable } from '../../../components/data-table/table-hook.js';
import { trialsApi } from '../../trials/trials.api.js';
import { trialQueryKeys } from '../../trials/trials.query-keys.js';
import { useTrialRandomization } from '../../trials/use-trial.js';
import { LogContactDialog, type ContactVisitOption } from '../../visits/patient-contacts/log-contact-dialog.js';
import { PatientTasksPanel } from '../../visits/patient-tasks/patient-tasks.js';
import { type VisitRegistrationTarget } from '../../visits/visit-registration/visit-registration.js';
import { visitQueryKeys } from '../../visits/visits.query-keys.js';
import { patientsApi } from '../patients.api.js';
import { patientQueryKeys } from '../patients.query-keys.js';
import {
  buildVisitRows,
  ChartParticipationBar,
  PatientVisitsTable,
  selectParticipation,
  type ScheduledVisitRow,
} from './chart-participation.js';
import { DecisionsPanel } from './decisions-panel/decisions-panel.js';
import { EnrollPatientDialog } from './enroll-patient-dialog/enroll-patient-dialog.js';
import { QualificationDialog } from './qualification-dialog/qualification-dialog.js';
import { AssignVisitsDialog } from './schedule-changes/assign-visits-dialog.js';
import { TemplateVisitsDialog } from './schedule-changes/template-visits-dialog.js';
import { ScheduleHistoryPanel } from './schedule-changes/schedule-history-panel.js';

export interface PatientPanelProps {
  canRead: boolean;
  canWrite: boolean;
  /**
   * `visits:write` — the booking action of the visit table (`REQ-VIS-015`).
   * Deliberately SEPARATE from `canWrite`: the chart form is `patients:write`,
   * while booking a visit is guarded by `visits:write` on the endpoint. Hiding
   * the button by a different key would either offer a 403 or hide a legal
   * action, so the UI gate mirrors the guard.
   */
  canScheduleVisits?: boolean;
  /**
   * `visits:read` — the open-task list of element 7 (`REQ-VIS-008`). Tasks are a
   * `visits` resource, so the read gate mirrors the endpoint guard (`visits:read`)
   * rather than the `patients:read` one that opens this panel. Without it, element
   * 7 stays hidden entirely.
   */
  canReadVisits?: boolean;
  /** Active trial of the cockpit, owned by `CockpitPage`. Optional — the panel works on the chart alone. */
  activeTrialId?: number | null;
  /**
   * Person picked anywhere in the cockpit (the participant list or this panel's
   * own site chart), owned by `CockpitPage`. `undefined` keeps the panel
   * standalone with its own pick state; a value (including `null`) makes the
   * cockpit the single source of truth.
   */
  selectedPatientId?: number | null;
  /** Notifies the cockpit that a person was picked inside this panel. */
  onSelectPatient?: (patientId: number) => void;
  /** Optional notification when a patient is enrolled into a trial. */
  onEnrollPatient?: (trialId: number) => void;
  /** Opens the kit registration dialog for a scheduled visit (REQ-KIT-014..026). */
  onRegisterKits?: (visit: ScheduledVisitRow, patient: PatientDto | undefined) => void;
  /**
   * Opens the visit registration window for a visit of the chart's plan
   * (REQ-VIS-021) - including one that was never booked. The cockpit owns the
   * window; the panel only maps the stored visit onto the window's target.
   */
  onRegisterVisit?: (target: VisitRegistrationTarget) => void;
}

const LIST_LIMIT = 25;

/** Columns whose header click re-sorts the site chart server-side (B3). */
const SORTABLE_COLUMNS = new Set<string>(['lastName', 'firstName', 'admissionBookNumber']);

const columnHelper = createAppColumnHelper<PatientDto>();

const PatientChartFormSchema = z.object({
  firstName: z.string().trim().min(1, 'Imię jest wymagane').max(255),
  lastName: z.string().trim().min(1, 'Nazwisko jest wymagane').max(255),
  birthDate: z.string(),
  pesel: z.string().max(32),
  sex: z.enum(['', 'Mężczyzna', 'Kobieta']),
  address: z.string(),
  admissionBookNumber: z.string().max(64),
  notes: z.string(),
});

type PatientChartFormInput = z.infer<typeof PatientChartFormSchema>;

const emptyForm: PatientChartFormInput = {
  firstName: '',
  lastName: '',
  birthDate: '',
  pesel: '',
  sex: '',
  address: '',
  admissionBookNumber: '',
  notes: '',
};

const fieldClass =
  'mt-1 w-full rounded-md border border-slate-700 bg-slate-950 px-2 py-1.5 text-xs text-slate-100';

const WARNING_TEXT: Record<PeselWarningCode, string> = {
  [PESEL_WARNINGS.INCOMPLETE]: 'Numer PESEL jest za krótki. Karta została zapisana.',
  [PESEL_WARNINGS.TOO_LONG]: 'Numer PESEL jest za długi. Karta została zapisana.',
  [PESEL_WARNINGS.CHECKSUM]: 'Suma kontrolna PESEL się nie zgadza. Karta została zapisana.',
  [PESEL_WARNINGS.UNREADABLE_DATE]: 'Z numeru PESEL nie da się odczytać daty urodzenia. Karta została zapisana.',
  [PESEL_WARNINGS.BIRTH_DATE_DIFFERS]: 'Data urodzenia różni się od daty w numerze PESEL. Karta została zapisana.',
};

function queryRetry(count: number, err: unknown): boolean {
  return isHTTPError(err) ? false : count < 3;
}

function displayValue(value: string | null): string {
  return value == null || value.length === 0 ? '—' : value;
}

function openDatePicker(event: { preventDefault: () => void; currentTarget: HTMLInputElement }): void {
  event.preventDefault();
  const input = event.currentTarget;
  if (input.disabled || typeof input.showPicker !== 'function') {
    return;
  }
  try {
    input.showPicker();
  } catch {
    input.focus();
  }
}

function blankToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

function toRegisterRequest(input: PatientChartFormInput): RegisterPatientRequest {
  return {
    firstName: input.firstName.trim(),
    lastName: input.lastName.trim(),
    birthDate: blankToNull(input.birthDate),
    pesel: blankToNull(input.pesel),
    sex: blankToNull(input.sex),
    address: blankToNull(input.address),
    admissionBookNumber: blankToNull(input.admissionBookNumber),
    notes: blankToNull(input.notes),
  };
}

export function PatientPanel({
  canRead,
  canWrite,
  canScheduleVisits = false,
  canReadVisits = false,
  // Active trial of the cockpit, owned by `CockpitPage` — the chart bar (element 6)
  // and the visit table (element 9) show the participation in this trial.
  activeTrialId = null,
  selectedPatientId,
  onSelectPatient,
  onEnrollPatient,
  onRegisterKits,
  onRegisterVisit,
}: PatientPanelProps): JSX.Element {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<PatientListSort | null>(null);
  const [sortOrder, setSortOrder] = useState<PatientListOrder>('asc');
  const [internalSelectedId, setInternalSelectedId] = useState<number | null>(null);
  // The cockpit owns the pick when it passes the prop; standalone, the panel does.
  const selectedId = selectedPatientId !== undefined ? selectedPatientId : internalSelectedId;
  const [saveWarnings, setSaveWarnings] = useState<{ patientId: number; warnings: PeselWarningCode[] } | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isEnrollDialogOpen, setIsEnrollDialogOpen] = useState(false);
  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false);
  const [isQualificationDialogOpen, setIsQualificationDialogOpen] = useState(false);
  // B6/B7 (issue #29): manual schedule changes of the active participation.
  const [isAssignVisitsOpen, setIsAssignVisitsOpen] = useState(false);
  const [isTemplateVisitsOpen, setIsTemplateVisitsOpen] = useState(false);

  const form = useForm<PatientChartFormInput>({
    resolver: zodResolver(PatientChartFormSchema),
    defaultValues: emptyForm,
  });

  const listFilters: PatientListQuery = useMemo(() => {
    const filters: PatientListQuery = { page, limit: LIST_LIMIT };
    const trimmed = search.trim();
    if (trimmed.length > 0) {
      filters.search = trimmed;
    }
    if (sort !== null) {
      filters.sort = sort;
      filters.order = sortOrder;
    }
    return filters;
  }, [page, search, sort, sortOrder]);

  const listQuery = useQuery({
    queryKey: patientQueryKeys.list(listFilters),
    queryFn: () => patientsApi.listPatients(listFilters),
    enabled: canRead,
    staleTime: 5 * 60 * 1000,
    retry: queryRetry,
  });

  const chartQuery = useQuery({
    queryKey: patientQueryKeys.detail(selectedId ?? 0),
    queryFn: () => patientsApi.getPatient(selectedId ?? 0),
    enabled: canRead && selectedId !== null,
    staleTime: 5 * 60 * 1000,
    retry: queryRetry,
  });

  // Elements 6 and 9: the participation of the selected person in the ACTIVE trial,
  // with the visit schedule generated from the protocol catalog (REQ-PAT-024).
  const enrollmentsQuery = useQuery({
    queryKey: patientQueryKeys.enrollments(selectedId ?? 0),
    queryFn: () => patientsApi.listPatientEnrollments(selectedId ?? 0),
    enabled: canRead && selectedId !== null,
    staleTime: 5 * 60 * 1000,
    retry: queryRetry,
  });

  // Visit names live in the Domain 2 catalog; the schedule itself only points at it.
  const catalogQuery = useQuery({
    queryKey: trialQueryKeys.visits(activeTrialId ?? 0),
    queryFn: () => trialsApi.listVisits(activeTrialId ?? 0),
    enabled: canRead && activeTrialId !== null,
    staleTime: 5 * 60 * 1000,
    retry: queryRetry,
  });

  /**
   * Preparation of everything elements 6 and 9 need for a person: their
   * participation history and (when a trial is picked) the protocol catalog.
   * React Query starts a query enabled during the same render as `isLoading`,
   * so a cold cache with `staleTime > 0` can freeze the bar on
   * "Ładowanie udziału pacjenta" — preparing the data first keeps the switch instant.
   */
  const prefetchParticipation = useCallback(
    async (patientId: number): Promise<void> => {
      await Promise.all([
        queryClient.prefetchQuery({
          queryKey: patientQueryKeys.enrollments(patientId),
          queryFn: () => patientsApi.listPatientEnrollments(patientId),
          staleTime: 5 * 60 * 1000,
        }),
                activeTrialId === null
          ? Promise.resolve()
          : queryClient.prefetchQuery({
              queryKey: trialQueryKeys.visits(activeTrialId),
              queryFn: () => trialsApi.listVisits(activeTrialId),
              staleTime: 5 * 60 * 1000,
            }),
      ]);
    },
    [queryClient, activeTrialId],
  );

  const selectPatient = useCallback(
    async (patientId: number): Promise<void> => {
      setInternalSelectedId(patientId);
      onSelectPatient?.(patientId);
      await prefetchParticipation(patientId);
    },
    [onSelectPatient, prefetchParticipation],
  );

  // An external pick (the participant list) arrives as a prop change — it goes
  // through the same preparation, never over a cold cache.
  useEffect(() => {
    if (selectedPatientId != null && canRead) {
      void prefetchParticipation(selectedPatientId);
    }
  }, [selectedPatientId, canRead, prefetchParticipation]);

  // Klik nowej kolumny → rosnąco; ponowne kliknięcie aktywnej → przełącza kierunek (B3).
  const handleSort = useCallback(
    (column: PatientListSort): void => {
      if (sort === column) {
        setSortOrder((order) => (order === 'asc' ? 'desc' : 'asc'));
      } else {
        setSort(column);
        setSortOrder('asc');
      }
      setPage(1);
    },
    [sort],
  );

  const columns = useMemo(() => {
    // Sortowanie jest server-side (lista jest stronicowana) — klik nagłówka zmienia
    // `listFilters.sort` i unieważnia zapytanie, zamiast sortować bieżącą stronę.
    const sortableHeader = (label: string, column: PatientListSort) => (): JSX.Element => (
      <button
        type="button"
        onClick={() => {
          handleSort(column);
        }}
        className="inline-flex items-center gap-1 text-left hover:text-slate-200"
      >
        {label}
        {sort === column ? (
          sortOrder === 'asc' ? (
            <ArrowUp className="h-3 w-3" aria-hidden="true" />
          ) : (
            <ArrowDown className="h-3 w-3" aria-hidden="true" />
          )
        ) : null}
      </button>
    );

    return columnHelper.columns([
      columnHelper.accessor('lastName', { header: sortableHeader('Nazwisko', 'lastName') }),
      columnHelper.accessor('firstName', { header: sortableHeader('Imię', 'firstName') }),
      columnHelper.accessor('admissionBookNumber', {
        header: sortableHeader('Numer księgi', 'admissionBookNumber'),
        cell: (info) => displayValue(info.getValue()),
      }),
      columnHelper.display({
        id: 'select',
        header: 'Wybór',
        cell: (info) => {
          const card = info.row.original;
          return (
            <button
              type="button"
              aria-label={`Wybierz ${card.firstName} ${card.lastName}`}
              onClick={() => {
                void selectPatient(card.id);
              }}
              className="rounded border border-cyan-800/60 px-2 py-1 text-xs text-cyan-300"
            >
              Wybierz
            </button>
          );
        },
      }),
    ]);
  }, [selectPatient, handleSort, sort, sortOrder]);

  const items = listQuery.data?.items ?? [];
  const table = useAppTable({ data: items, columns });
  const chart = chartQuery.data;
  const participation =
    activeTrialId === null
      ? null
      : selectParticipation(enrollmentsQuery.data?.items ?? [], activeTrialId);

  // B.1/B.2: the active trial decides whether the chart bar may show a randomization number.
  const randomizationEnabled = useTrialRandomization(activeTrialId);
  // Presentation label of the B6/B7 dialogs („badanie PROTOCOL-2026-A") —
  // shares the trial-detail cache with `useTrialRandomization` and runs only
  // while a participation is open.
  const trialDetailQuery = useQuery({
    queryKey: trialQueryKeys.detail(activeTrialId ?? 0),
    queryFn: () => trialsApi.getTrial(activeTrialId ?? 0),
    enabled: canRead && activeTrialId !== null && participation !== null,
    staleTime: 5 * 60 * 1000,
  });
  const trialLabel = trialDetailQuery.data?.protocolNumber ?? '—';
  const selectedPatientName = chart ? `${chart.firstName} ${chart.lastName}` : null;
  // Element 7 hangs on the participation of the ACTIVE trial (REQ-VIS-008); the
  // contact dialog offers the plan rows of that participation as "Powiąż z wizytą".
  const taskEnrollmentId = participation?.enrollment.id ?? null;
  const contactVisitOptions: ContactVisitOption[] =
    participation === null
      ? []
      : buildVisitRows(participation.plan, catalogQuery.data ?? []).map((row) => ({
          id: row.id,
          label: row.label,
        }));
  const warnings =
    saveWarnings && chart && saveWarnings.patientId === chart.id ? saveWarnings.warnings : [];

  async function onSave(input: PatientChartFormInput): Promise<void> {
    setSaveError(null);
    setIsSaving(true);
    try {
      const result = await patientsApi.registerPatient(toRegisterRequest(input));
      setSaveWarnings({ patientId: result.patient.id, warnings: result.pesel.warnings });
      form.reset(emptyForm);
      await queryClient.invalidateQueries({ queryKey: patientQueryKeys.lists() });
      queryClient.setQueryData(patientQueryKeys.detail(result.patient.id), result.patient);
      await selectPatient(result.patient.id);
    } catch (error: unknown) {
      setSaveError(await getApiErrorMessage(error, 'Nie udało się zapisać karty.'));
    } finally {
      setIsSaving(false);
    }
  }

    /**
   * REQ-VIS-015: the visit was re-booked. Two read paths are stale now — the
   * chart history (its rows carry the planned day) and EVERY day plan, because
   * the new day can be any day and the panel does not know which one the
   * coordinator is looking at. Invalidation is by prefix, so no concrete day is
   * guessed.
   */
  async function onVisitScheduled(): Promise<void> {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: patientQueryKeys.enrollments(selectedId ?? 0) }),
      queryClient.invalidateQueries({ queryKey: visitQueryKeys.dayPlans() }),
    ]);
  }

  /**
   * REQ-PAT-024/033 (issue #29 B6/B7): a manual schedule change rewrites the
   * plan (the enrollments cache feeds the visit table) and appends to the
   * change log. A REMOVAL can also empty a day-plan row (the visit may have
   * had a booked day) — day plans invalidate by prefix, no concrete day is
   * guessed; an addition creates a visit without a day, so day plans stay
   * untouched.
   */
  async function onScheduleChanged(removed: boolean): Promise<void> {
    const invalidations = [
      queryClient.invalidateQueries({
        queryKey: patientQueryKeys.enrollments(selectedId ?? 0),
      }),
      queryClient.invalidateQueries({
        queryKey: patientQueryKeys.scheduleLog(
          selectedId ?? 0,
          participation?.enrollment.id ?? 0,
        ),
      }),
    ];
    if (removed) {
      invalidations.push(
        queryClient.invalidateQueries({ queryKey: visitQueryKeys.dayPlans() }),
      );
    }
    await Promise.all(invalidations);
  }

  /**
   * REQ-VIS-021: maps a visit of the chart's plan onto the registration
   * window's target. `scheduledDate` stays null for a visit that was never
   * booked - the window opens in register mode either way (the backend does
   * not require a booking). The chart entry has no day-plan row, so the
   * procedures entry stays closed here; it is reachable from the day plan
   * once the registered visit shows up there.
   */
  function toRegistrationTarget(visit: PatientVisitDto): VisitRegistrationTarget {
    const position = (catalogQuery.data ?? []).find((item) => item.id === visit.trialVisitId);
    return {
      visitId: visit.id,
      visitLabel: position ? `V${position.visitNumber} ${visitLabel(position.visitName1, position.visitName2)}` : '—',
      patientName: chart ? `${chart.firstName} ${chart.lastName}` : 'Pacjent',
      scheduledDate: visit.dateScheduled,
      scheduledTime: visit.timeScheduled,
      performedDate: visit.dateReal,
      performedTime: visit.timeReal,
      currentType: visit.type,
      currentComment: visit.mainComment,
      dayPlanRow: null,
    };
  }

  if (!canRead) {
    return (
      <section role="alert" className="rounded-lg border border-slate-800 bg-slate-900/60 p-8">
        <h2 className="text-base font-semibold text-slate-100">Brak dostępu do kart pacjentów</h2>
        <p className="mt-2 text-xs text-slate-400">Nie masz uprawnień do kartoteki pacjentów tego ośrodka.</p>
      </section>
    );
  }

  return (
    <div className="grid grid-cols-12 gap-4">
      <section aria-label="Pacjenci ośrodka" className="col-span-4 space-y-3">
        <h2 className="text-sm font-semibold text-slate-100">Pacjenci ośrodka</h2>
        <div className="relative">
          <Search className="pointer-events-none absolute top-2 left-2.5 h-3.5 w-3.5 text-slate-500" />
          <input
            type="text"
            value={search}
            aria-label="Szukaj pacjenta"
            placeholder="Szukaj po nazwisku, imieniu, numerze księgi, adresie lub telefonie…"
            onChange={(event) => {
              setSearch(event.target.value);
              setPage(1);
            }}
            className="w-full rounded-md border border-slate-700 bg-slate-950 py-1.5 pr-3 pl-8 text-xs text-slate-100"
          />
        </div>
        {listQuery.isLoading ? (
          <p className="text-xs text-slate-400">Ładowanie listy pacjentów</p>
        ) : listQuery.isError ? (
          <section role="alert" className="rounded-lg border border-rose-800/70 bg-rose-950/40 p-4">
            <p className="text-xs text-rose-200">Nie udało się wczytać listy pacjentów.</p>
            <button
              type="button"
              onClick={() => {
                void listQuery.refetch();
              }}
              className="mt-2 rounded-md border border-rose-700 px-3 py-1.5 text-xs text-rose-200"
            >
              Spróbuj ponownie
            </button>
          </section>
        ) : items.length === 0 ? (
          <p className="text-xs text-slate-400">Brak kart w ośrodku.</p>
        ) : (
          <div className="overflow-hidden rounded-lg border border-slate-800">
            <table className="w-full text-left text-xs">
              <thead>
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id} className="border-b border-slate-800 bg-slate-950/60">
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        scope="col"
                        aria-sort={
                          SORTABLE_COLUMNS.has(header.column.id)
                            ? header.column.id === sort
                              ? sortOrder === 'asc'
                                ? 'ascending'
                                : 'descending'
                              : 'none'
                            : undefined
                        }
                        className="px-3 py-2 text-slate-400"
                      >
                        <table.FlexRender header={header} />
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody>
                {table.getRowModel().rows.map((row) => (
                  <tr key={row.id} className="border-b border-slate-800/80">
                    {row.getAllCells().map((cell) => (
                      <td key={cell.id} className="px-3 py-2">
                        <table.FlexRender cell={cell} />
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {listQuery.data && (listQuery.data.page > 1 || listQuery.data.hasNext) ? (
          <div className="flex gap-2">
            <button
              type="button"
              disabled={listQuery.data.page <= 1}
              onClick={() => setPage((current) => Math.max(1, current - 1))}
              className="rounded border border-slate-700 px-2 py-1 text-xs text-slate-300 disabled:opacity-40"
            >
              Poprzednia strona
            </button>
            <button
              type="button"
              disabled={!listQuery.data.hasNext}
              onClick={() => setPage((current) => current + 1)}
              className="rounded border border-slate-700 px-2 py-1 text-xs text-slate-300 disabled:opacity-40"
            >
              Następna strona
            </button>
          </div>
        ) : null}
      </section>

      <div className="col-span-8 space-y-4">
        <section aria-label="Karta pacjenta" className="rounded-lg border border-slate-800 bg-slate-900 p-4">
          <h2 className="text-sm font-semibold text-slate-100">Karta pacjenta</h2>
          {chartQuery.isLoading ? (
            <p className="mt-3 text-xs text-slate-400">Ładowanie karty</p>
          ) : chartQuery.isError ? (
            <p role="alert" className="mt-3 text-xs text-rose-200">
              Nie udało się wczytać karty.
            </p>
          ) : chart ? (
            <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-2 text-xs">
              <div>
                <dt className="text-slate-400">Imię i nazwisko</dt>
                <dd className="text-slate-100">
                  {chart.firstName} {chart.lastName}
                </dd>
              </div>
              <div>
                <dt className="text-slate-400">Data urodzenia</dt>
                <dd className="text-slate-100">{displayValue(chart.birthDate)}</dd>
              </div>
              <div>
                <dt className="text-slate-400">PESEL</dt>
                <dd className="text-slate-100">{displayValue(chart.pesel)}</dd>
              </div>
              <div>
                <dt className="text-slate-400">Płeć</dt>
                <dd className="text-slate-100">{displayValue(chart.sex)}</dd>
              </div>
              <div>
                <dt className="text-slate-400">Adres</dt>
                <dd className="text-slate-100">{displayValue(chart.address)}</dd>
              </div>
              <div>
                <dt className="text-slate-400">Numer księgi</dt>
                <dd className="text-slate-100">{displayValue(chart.admissionBookNumber)}</dd>
              </div>
              <div className="col-span-2">
                <dt className="text-slate-400">Notatki</dt>
                <dd className="text-slate-100">{displayValue(chart.notes)}</dd>
              </div>
            </dl>
          ) : (
            <p className="mt-3 text-xs text-slate-400">Wybierz osobę z listy albo zapisz nową kartę.</p>
          )}
          {chart ? (
            <ChartParticipationBar
              trialPicked={activeTrialId !== null}
              isLoading={enrollmentsQuery.isLoading}
              isError={enrollmentsQuery.isError}
              participation={participation}
              randomizationEnabled={randomizationEnabled}
              canEnroll={canWrite}
              onEnroll={() => setIsEnrollDialogOpen(true)}
              onQualification={() => setIsQualificationDialogOpen(true)}
              onRetry={() => {
                void enrollmentsQuery.refetch();
              }}
            />
          ) : null}
          {chart && participation ? (
            <DecisionsPanel
              key={participation.enrollment.id}
              patientId={chart.id}
              patientName={`${chart.firstName} ${chart.lastName}`}
              enrollmentId={participation.enrollment.id}
              canWrite={canWrite}
            />
          ) : null}
          {warnings.length > 0 ? (
            <ul className="mt-3 space-y-1 text-xs text-amber-300">
              {warnings.map((code) => (
                <li key={code}>{WARNING_TEXT[code]}</li>
              ))}
            </ul>
          ) : null}

          {canWrite ? (
            <form
              className="mt-4 grid grid-cols-2 gap-3 border-t border-slate-800 pt-4"
              onSubmit={(event) => {
                void form.handleSubmit(onSave)(event);
              }}
            >
              <h3 className="col-span-2 text-xs font-semibold text-slate-200">Nowa karta</h3>
              <label className="text-xs text-slate-400">
                Imię
                <input className={fieldClass} {...form.register('firstName')} />
              </label>
              <label className="text-xs text-slate-400">
                Nazwisko
                <input className={fieldClass} {...form.register('lastName')} />
              </label>
              <label className="text-xs text-slate-400">
                Data urodzenia
                <input
                  type="date"
                  className={`${fieldClass} scheme-dark cursor-pointer`}
                  {...form.register('birthDate')}
                  onClick={openDatePicker}
                  onKeyDown={(event) => {
                    if (event.key !== 'Tab' && event.key !== 'Escape') {
                      event.preventDefault();
                    }
                  }}
                />
              </label>
              <label className="text-xs text-slate-400">
                PESEL
                <input className={fieldClass} {...form.register('pesel')} />
              </label>
              <label className="text-xs text-slate-400">
                Płeć
                <select className={`${fieldClass} scheme-dark`} {...form.register('sex')}>
                  <option value="">—</option>
                  <option value="Mężczyzna">Mężczyzna</option>
                  <option value="Kobieta">Kobieta</option>
                </select>
              </label>
              <label className="text-xs text-slate-400">
                Numer księgi
                <input className={fieldClass} {...form.register('admissionBookNumber')} />
              </label>
              <label className="col-span-2 text-xs text-slate-400">
                Adres
                <input className={fieldClass} {...form.register('address')} />
              </label>
              <label className="col-span-2 text-xs text-slate-400">
                Notatki
                <input className={fieldClass} {...form.register('notes')} />
              </label>
              {form.formState.errors.firstName ? (
                <p className="text-xs text-rose-300">{form.formState.errors.firstName.message}</p>
              ) : null}
              {form.formState.errors.lastName ? (
                <p className="text-xs text-rose-300">{form.formState.errors.lastName.message}</p>
              ) : null}
              {saveError ? (
                <p role="alert" className="col-span-2 text-xs text-rose-300">
                  {saveError}
                </p>
              ) : null}
              <div className="col-span-2">
                <button
                  type="submit"
                  disabled={isSaving}
                  className="rounded-md bg-cyan-600 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-cyan-500 disabled:opacity-50"
                >
                  Zapisz kartę
                </button>
              </div>
            </form>
          ) : null}
        </section>

        <PatientTasksPanel
          enrollmentId={taskEnrollmentId}
          trialPicked={activeTrialId !== null}
          canRead={canReadVisits}
          canWrite={canScheduleVisits}
          patientName={selectedPatientName}
        />

        <section aria-label="Wizyty pacjenta" className="rounded-lg border border-slate-800 bg-slate-900 p-4">
          <div className="flex items-center justify-between gap-2">
            <h2 className="text-sm font-semibold text-slate-100">
              {canWrite ? 'Wizyty pacjenta' : 'Wizyty pacjenta (tylko do odczytu)'}
            </h2>
            {/* B6 (issue #29): the catalog addition is a `patients:write` action
                and needs an open participation — without either, the control is
                hidden (wireframe #add-visit-open). */}
            {canWrite && chart && participation ? (
              <div className="flex items-center gap-2">
                {/* #41: the preset window is a `patients:write` action, gated
                    exactly like the catalog assignment next to it. */}
                <button
                  type="button"
                  onClick={() => setIsTemplateVisitsOpen(true)}
                  className="rounded border border-cyan-700/70 bg-cyan-950/40 px-2.5 py-1 text-xs font-medium text-cyan-300 hover:bg-cyan-900/60"
                >
                  Wizyty szablonowe
                </button>
                <button
                  type="button"
                  onClick={() => setIsAssignVisitsOpen(true)}
                  className="rounded border border-cyan-700/70 bg-cyan-950/40 px-2.5 py-1 text-xs font-medium text-cyan-300 hover:bg-cyan-900/60"
                >
                  Przypisz wizyty
                </button>
              </div>
            ) : null}
          </div>
          {/* Read-only viewer of an open participation (wireframe „Brak uprawnień”). */}
          {!canWrite && chart && participation ? (
            <p className="mt-2 text-xs text-slate-400">
              <span className="font-medium text-slate-300">
                Nie masz uprawnienia do zapisu pacjentów.
              </span>{' '}
              Harmonogram i historia zmian są widoczne do odczytu.
            </p>
          ) : null}
          <PatientVisitsTable
            trialPicked={activeTrialId !== null}
            isLoading={enrollmentsQuery.isLoading}
            isError={enrollmentsQuery.isError}
            participation={participation}
            catalog={catalogQuery.data ?? []}
            canWrite={canScheduleVisits}
            onVisitScheduled={() => {
              void onVisitScheduled();
            }}
            onRegisterVisit={
              onRegisterVisit ? (visit) => onRegisterVisit(toRegistrationTarget(visit)) : undefined
            }
            onRegisterKits={onRegisterKits ? (visit) => onRegisterKits(visit, chart) : undefined}
            onRetry={() => {
              void enrollmentsQuery.refetch();
            }}
          />
          {/* B6/B7 (issue #29): the change log is inline under the visit table
              (gate decision 3) and read-only for EVERY viewer of the section. */}
          {chart && participation ? (
            <ScheduleHistoryPanel
              key={participation.enrollment.id}
              patientId={chart.id}
              enrollmentId={participation.enrollment.id}
              catalog={catalogQuery.data ?? []}
            />
          ) : null}
          {canScheduleVisits ? (
            <div className="mt-3 flex justify-end">
              <button
                type="button"
                id="log-contact"
                onClick={() => setIsContactDialogOpen(true)}
                className="rounded-md border border-cyan-700/70 bg-cyan-950/40 px-3.5 py-1.5 text-xs font-medium text-cyan-300 hover:bg-cyan-900/60"
              >
                Kontakt SMS/telefon
              </button>
            </div>
          ) : null}
        </section>
      </div>

      {isContactDialogOpen ? (
        <LogContactDialog
          visits={contactVisitOptions}
          patientName={selectedPatientName}
          onSaved={() => setIsContactDialogOpen(false)}
          onClose={() => setIsContactDialogOpen(false)}
        />
      ) : null}

      {isEnrollDialogOpen && chart ? (
        <EnrollPatientDialog
          patientId={chart.id}
          patientName={`${chart.firstName} ${chart.lastName}`}
          activeTrialId={activeTrialId ?? null}
          onSaved={(response) => {
            setIsEnrollDialogOpen(false);
            onEnrollPatient?.(response.enrollment.trialId);
          }}
          onClose={() => setIsEnrollDialogOpen(false)}
        />
      ) : null}

      {isQualificationDialogOpen && chart && participation ? (
        <QualificationDialog
          key={participation.enrollment.id}
          patientId={chart.id}
          patientName={`${chart.firstName} ${chart.lastName}`}
          enrollmentId={participation.enrollment.id}
          canWrite={canWrite}
          onClose={() => setIsQualificationDialogOpen(false)}
        />
      ) : null}

      {isAssignVisitsOpen && chart && participation ? (
        <AssignVisitsDialog
          key={participation.enrollment.id}
          patientId={chart.id}
          enrollmentId={participation.enrollment.id}
          trialId={participation.enrollment.trialId}
          patientName={`${chart.firstName} ${chart.lastName}`}
          trialLabel={trialLabel}
          catalog={catalogQuery.data ?? []}
          plan={participation.plan}
          onChanged={(kind) => {
            // An unassign can empty a day-plan row; an assign never carries a
            // booked day — the kind keeps the invalidation precise.
            void onScheduleChanged(kind === 'unassign');
          }}
          onClose={() => setIsAssignVisitsOpen(false)}
        />
      ) : null}

      {isTemplateVisitsOpen && chart && participation ? (
        <TemplateVisitsDialog
          key={participation.enrollment.id}
          patientId={chart.id}
          enrollmentId={participation.enrollment.id}
          patientName={`${chart.firstName} ${chart.lastName}`}
          trialLabel={trialLabel}
          onAdded={() => {
            // #41: preset visits are created WITHOUT a booked day — the same
            // invalidation as an assign (schedule + change log; day plans stay
            // untouched).
            void onScheduleChanged(false);
          }}
          onClose={() => setIsTemplateVisitsOpen(false)}
        />
      ) : null}
    </div>
  );
}
